// signals-web.ts — 1:1 JS port of src-tauri/src/signals.rs: the derived size-logistics signals
// the Sizing Recap and "Ask about sizes" narrate. 🔴 WHY: compute_signals was a zero stub in
// web-commands, so the ask answered "Log a few sizes first — there's nothing to ask about yet"
// with three sizes ON SCREEN (measured live 2026-08-19). Where this and the Rust could drift,
// the Rust wins.
//
// The CHILDREN-domain fence travels with the math: every figure is a SIZE, a DATE, a COUNT, or
// arithmetic between them. Nothing here is health, growth percentiles, or development — those
// concepts do not exist in this file, same as the Rust.

import type { Child, SizeRecord, InventoryItem } from "./store";

const round1 = (x: number) => Math.round(x * 10) / 10;
const round2 = (x: number) => Math.round(x * 100) / 100;
const parseDay = (d: string) => (/^\d{4}-\d{2}-\d{2}/.test(d ?? "") ? d.slice(0, 10) : null);
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);
const monthsBetween = (a: string, b: string) => round1(dayDiff(a, b) / 30.4375);
const yearOf = (d: string) => Number(d.slice(0, 4));

function cap(s: string): string { return s ? s[0].toUpperCase() + s.slice(1) : s; }
function trimNum(x: number): string {
  const r = round2(x);
  return Number.isInteger(r) ? String(r) : String(r).replace(/0+$/, "").replace(/\.$/, "");
}

export function computeSignalsWeb(child: Child, records: SizeRecord[], inventory: InventoryItem[], today: string) {
  const dated = records
    .filter((r) => r.child_id === child.id && parseDay(r.recorded_on))
    .map((r) => ({ d: r.recorded_on.slice(0, 10), r }))
    .sort((a, b) => a.d.localeCompare(b.d));
  const inv = inventory.filter((i) => i.child_id === child.id);

  const current_age_years = child.birth_year != null
    ? (() => { const y = yearOf(today) - child.birth_year!; return y >= 0 && y <= 25 ? y : null; })()
    : null;

  const byCat = new Map<string, { d: string; r: SizeRecord }[]>();
  for (const e of dated) byCat.set(e.r.category, [...(byCat.get(e.r.category) ?? []), e]);

  const first = dated[0]?.d ?? null;
  const last = dated[dated.length - 1]?.d ?? null;
  const overall = {
    total_records: dated.length,
    total_inventory: inv.length,
    categories_tracked: byCat.size,
    first_recorded: first,
    last_recorded: last,
    days_span: first && last ? dayDiff(first, last) + 1 : 0,
    unit_note: "size_value is the numeric form of the human size_label for the same category, used only for arithmetic; sizes are logistics, not a growth measure.",
  };

  // current size per category (BTreeMap order = category-sorted, matched by sorting)
  const cats = [...byCat.keys()].sort();
  const current_sizes = cats.map((cat) => {
    const recs = byCat.get(cat)!;
    const { d, r } = recs[recs.length - 1];
    return {
      category: cat, size_label: r.size_label, size_value: r.size_value, brand: r.brand,
      recorded_on: d, months_since: monthsBetween(d, today),
    };
  });
  const currentByCat = new Map(current_sizes.map((c) => [c.category, { value: c.size_value, label: c.size_label }]));

  // velocities (≥2 numeric records)
  const velocities: {
    category: string; earliest_label: string; earliest_value: number; earliest_on: string;
    latest_label: string; latest_value: number; latest_on: string; months_elapsed: number;
    sizes_gained: number; sizes_per_month: number; records_in_category: number;
  }[] = [];
  for (const cat of cats) {
    const recs = byCat.get(cat)!;
    const numeric = recs.filter((e) => e.r.size_value != null);
    if (numeric.length < 2) continue;
    const e0 = numeric[0], e1 = numeric[numeric.length - 1];
    const months = monthsBetween(e0.d, e1.d);
    if (months <= 0) continue;
    const gained = round2(e1.r.size_value! - e0.r.size_value!);
    velocities.push({
      category: cat,
      earliest_label: e0.r.size_label, earliest_value: e0.r.size_value!, earliest_on: e0.d,
      latest_label: e1.r.size_label, latest_value: e1.r.size_value!, latest_on: e1.d,
      months_elapsed: months, sizes_gained: gained, sizes_per_month: round2(gained / months),
      records_in_category: recs.length,
    });
  }
  velocities.sort((a, b) => Math.abs(b.sizes_per_month) - Math.abs(a.sizes_per_month));

  // brand discrepancies (largest gap per unordered brand pair, per category)
  const brand_discrepancies: {
    category: string; brand_a: string; size_a_label: string; size_a_value: number;
    brand_b: string; size_b_label: string; size_b_value: number; size_gap: number; days_apart: number;
  }[] = [];
  for (const cat of cats) {
    const branded = byCat.get(cat)!.filter((e) => e.r.brand.trim() && e.r.size_value != null);
    const best = new Map<string, (typeof brand_discrepancies)[number]>();
    for (let i = 0; i < branded.length; i++) {
      for (let jj = i + 1; jj < branded.length; jj++) {
        const A = branded[i], B = branded[jj];
        if (A.r.brand.toLowerCase() === B.r.brand.toLowerCase()) continue;
        const gap = round2(Math.abs(A.r.size_value! - B.r.size_value!));
        if (gap === 0) continue;
        const key = [A.r.brand, B.r.brand].sort().join(" ");
        const cand = {
          category: cat,
          brand_a: A.r.brand, size_a_label: A.r.size_label, size_a_value: A.r.size_value!,
          brand_b: B.r.brand, size_b_label: B.r.size_label, size_b_value: B.r.size_value!,
          size_gap: gap, days_apart: Math.abs(dayDiff(A.d, B.d)),
        };
        const cur = best.get(key);
        if (!cur || gap > cur.size_gap) best.set(key, cand);
      }
    }
    brand_discrepancies.push(...best.values());
  }
  brand_discrepancies.sort((a, b) => b.size_gap - a.size_gap);
  brand_discrepancies.length = Math.min(brand_discrepancies.length, 6);

  // age milestones
  const age_milestones: { category: string; size_label: string; age_years: number; recorded_on: string }[] = [];
  if (child.birth_year != null) {
    for (const cat of cats) {
      const recs = byCat.get(cat)!;
      const { d, r } = recs[recs.length - 1];
      const age = yearOf(d) - child.birth_year;
      if (age >= 0 && age <= 25) age_milestones.push({ category: cat, size_label: r.size_label, age_years: age, recorded_on: d });
    }
    age_milestones.sort((a, b) => a.category.localeCompare(b.category));
  }

  // ahead-bought vs current
  const ahead_bought = inv.map((item) => {
    const cur = currentByCat.get(item.category) ?? { value: null, label: "" };
    const sizes_ahead = item.size_value != null && cur.value != null ? round2(item.size_value - cur.value) : null;
    const fit = sizes_ahead == null ? "unknown" : sizes_ahead > 0.25 ? "ahead" : sizes_ahead < -0.25 ? "outgrown" : "fits now";
    return {
      item_id: item.id, category: item.category, size_label: item.size_label, size_value: item.size_value,
      description: item.description, for_season: item.for_season,
      current_size_label: cur.label || null, current_size_value: cur.value,
      sizes_ahead, fit,
    };
  }).sort((a, b) => (a.sizes_ahead ?? Number.MAX_VALUE) - (b.sizes_ahead ?? Number.MAX_VALUE));

  // headline facts (1:1 sentences with the Rust)
  const facts: { kind: string; text: string; tone: string }[] = [];
  if (current_sizes.length) {
    const c = [...current_sizes].sort((a, b) => a.months_since - b.months_since)[0];
    const m = Math.round(c.months_since);
    const when = c.months_since < 1 ? "logged this month" : `logged ${m} month${m === 1 ? "" : "s"} ago`;
    const brand = c.brand.trim() ? ` (${c.brand.trim()})` : "";
    facts.push({ kind: "current", tone: "neutral", text: `Current ${c.category.toLowerCase()} size is ${c.size_label}${brand} — ${when}.` });
  }
  const v = velocities[0];
  if (v) {
    const dir = v.sizes_per_month >= 0 ? "up" : "down";
    const perMo = Math.abs(v.sizes_per_month);
    facts.push({ kind: "velocity", tone: "neutral", text: `${cap(v.category)} sizes have moved ${dir} fastest — from ${v.earliest_label} to ${v.latest_label} over ${Math.round(v.months_elapsed)} months, about ${perMo.toFixed(2)} size${Math.abs(perMo - 1) < Number.EPSILON ? "" : "s"} per month.` });
  }
  const b = brand_discrepancies[0];
  if (b) {
    facts.push({ kind: "brand", tone: "neutral", text: `In ${b.category.toLowerCase()}, ${b.brand_a} (${b.size_a_label}) and ${b.brand_b} (${b.size_b_label}) sit ${trimNum(b.size_gap)} size${Math.abs(b.size_gap - 1) < Number.EPSILON ? "" : "s"} apart for the same child.` });
  }
  const a = ahead_bought.find((x) => x.fit === "ahead" && x.sizes_ahead != null);
  if (a) {
    const n = a.sizes_ahead!;
    const what = a.description.trim() ? a.description.trim().toLowerCase() : a.category.toLowerCase();
    facts.push({ kind: "ahead", tone: "ahead", text: `The stashed ${what} (size ${a.size_label}) is ${trimNum(n)} size${Math.abs(n - 1) < Number.EPSILON ? "" : "s"} ahead of the current ${a.category.toLowerCase()} size ${a.current_size_label ?? "?"}.` });
  }
  const fitsNow = ahead_bought.filter((x) => x.fit === "fits now").length;
  if (fitsNow > 0) {
    facts.push({ kind: "ahead", tone: "now", text: `${fitsNow} bought-ahead item${fitsNow === 1 ? "" : "s"} now match${fitsNow === 1 ? "es" : ""} the current size.` });
  }
  if (overall.total_records >= 2 && overall.days_span > 0) {
    const months = Math.round(overall.days_span / 30.4375);
    facts.push({ kind: "span", tone: "neutral", text: `${overall.total_records} sizes logged across ${overall.categories_tracked} categor${overall.categories_tracked === 1 ? "y" : "ies"} over about ${months} month${months === 1 ? "" : "s"}.` });
  }

  return {
    schema:
      "oriora.outgrown.v1-signals: derived clothing/shoe SIZE logistics for one child (no raw notes). Every figure is a size, a date, a count, or arithmetic between them (size change per elapsed month, size gap between brands, sizes a stashed item is ahead of the current size). current_sizes is the latest size per category; velocities is size change per elapsed month (sorted fastest-changing first); brand_discrepancies is the size gap between two brands logged for the same category; ahead_bought compares a stashed item to the current size; headline_facts is a pre-ranked list of ready-to-say observational sentences already containing the figures — LEAD with these, weave them into a short plain-words recap, do not re-derive numbers. There is NO health, growth-percentile, development, or medical content — sizes are logistics only. Narrate the figures as plain observations; never advise, never say a change is normal/abnormal/fast/slow/concerning, never frame anything as growth or development.",
    generated_for: today,
    child_name: child.name,
    current_age_years,
    overall,
    current_sizes,
    velocities,
    brand_discrepancies,
    age_milestones,
    ahead_bought,
    headline_facts: facts,
  };
}
